import { useCallback, useRef, useState } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";
import { useT } from "../i18n";

/**
 * 회전 단추 한 번 = 45°(rad) · 끌기 1px 당 회전(rad).
 * 방향(2026-10-11 고침): 렌더러 heading 이 커지면 화면 속 장면은 시계 방향으로 돈다 — 그래서 ↺(반시계)는 heading 을 줄이고,
 * 끌기는 손가락을 따라가게(오른쪽으로 끌면 앞쪽 물체가 오른쪽으로) heading 을 줄인다.
 */
export const ROTATE_STEP = Math.PI / 4;
export const DRAG_RAD_PER_PX = 0.005;
/** 보는 방향이 고른 방향을 따라가는 빠르기(1/s) — 렌더러는 눈 위치를 직선으로 따라가서, 큰 각을 한 번에 주면 장면 가운데를 가로지른다 */
const FOLLOW = 8;

/**
 * 3D 화면 회전(2026-10-10 칠판 뷰 → 2026-10-11 학생 룸도) — 고른 방향(target)을 그리는 방향(cur)이 매 프레임 부드럽게 따라간다.
 * step(now ms) 를 rAF 마다 불러 지금 돌린 각(rad)을 받는다 · canvas 에 끌기 손잡이를 붙인다 · 이 화면에만(저장 안 함).
 */
export function useViewTurn() {
  const h = useRef({ target: 0, cur: 0, last: -1 });
  const [turned, setTurned] = useState(false);
  const turn = useCallback((to: number) => {
    h.current.target = to;
    const off = to - Math.round(to / (2 * Math.PI)) * 2 * Math.PI;
    setTurned(Math.abs(off) > 1e-6);
  }, []);
  const step = useCallback((now: number) => {
    const s = h.current;
    const dt = s.last < 0 ? 0 : (now - s.last) / 1000;
    s.last = now;
    s.cur += (s.target - s.cur) * Math.min(1, dt * FOLLOW);
    if (Math.abs(s.target - s.cur) < 1e-4) s.cur = s.target;
    return s.cur;
  }, []);
  const target = useCallback(() => h.current.target, []);
  const drag = useRef<{ id: number; x: number } | null>(null);
  const canvasHandlers = {
    onPointerDown: (e: ReactPointerEvent<HTMLElement>) => {
      drag.current = { id: e.pointerId, x: e.clientX };
      e.currentTarget.setPointerCapture?.(e.pointerId);
    },
    onPointerMove: (e: ReactPointerEvent<HTMLElement>) => {
      const d = drag.current;
      if (!d || d.id !== e.pointerId) return;
      turn(h.current.target - (e.clientX - d.x) * DRAG_RAD_PER_PX);
      d.x = e.clientX;
    },
    onPointerUp: () => void (drag.current = null),
    onPointerCancel: () => void (drag.current = null),
  };
  return { step, turn, turned, target, canvasHandlers };
}

export type ViewTurn = ReturnType<typeof useViewTurn>;

/** ↺ · ↻(45°) · ⟲ 처음 방향(돌렸을 때만) — 확대 · 축소(.pf-zoom) 아래 */
export function RotateControls({ turn: v, testId }: { turn: ViewTurn; testId: string }) {
  const t = useT();
  return (
    <div
      className="pf-zoom pf-rotate"
      role="group"
      aria-label={t("common.rotate")}
      data-testid={testId}
    >
      <button
        type="button"
        className="pf-zoom__btn"
        aria-label={t("common.rotateLeft")}
        title={t("common.rotateLeft")}
        data-testid={`${testId}-left`}
        onClick={() => v.turn(v.target() - ROTATE_STEP)}
      >
        ↺
      </button>
      <button
        type="button"
        className="pf-zoom__btn"
        aria-label={t("common.rotateRight")}
        title={t("common.rotateRight")}
        data-testid={`${testId}-right`}
        onClick={() => v.turn(v.target() + ROTATE_STEP)}
      >
        ↻
      </button>
      {v.turned && (
        <button
          type="button"
          className="pf-zoom__btn"
          aria-label={t("common.rotateReset")}
          title={t("common.rotateReset")}
          data-testid={`${testId}-reset`}
          // 가장 가까운 '처음 방향'(2π 배수)으로 — 몇 바퀴 돌렸어도 되감지 않는다
          onClick={() => v.turn(Math.round(v.target() / (2 * Math.PI)) * 2 * Math.PI)}
        >
          ⟲
        </button>
      )}
    </div>
  );
}
